import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { TextInput } from '@/components/ui/TextInput';
import { TextArea } from '@/components/ui/TextArea';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Icon } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import { agentApi, type AiCallSummary } from '@/shared/api/agentApi';
import { useSipStore } from '../stores/sipStore';
import { NotesChat } from './NotesChat';

export interface DispositionOption {
  code: string;
  label: string;
  color?: string;
  requires_followup?: boolean;
  /** When true, the agent must write a note before this code can be saved. */
  requires_note?: boolean;
  /** סיבות בתוך הסטטוס (אפיון 2.8). כשקיימות — הנציג חייב לבחור אחת. */
  reasons?: Array<{ code: string; label: string }>;
  alert_before_followup?: boolean;
  /** `deal_closed` מסמן עסקה — ואז נפתח בורר המוצר (אפיון 6.9). */
  lead_status?: string | null;
}

/** מוצר לבחירה בסגירת עסקה. שם בלבד — המחיר לעולם אינו מגיע לנציג (6.9). */
export interface ProductOption {
  id: number;
  name: string;
}

export interface TagOption {
  slug: string;
  label: string;
  color?: string;
}

export interface WrapupInlineProps {
  dispositions: DispositionOption[];
  /** Admin-defined call tags shown as toggleable chips. */
  tagOptions?: TagOption[];
  onSave: (data: { disposition: string; notes: string; callback_at: string | null; tags: string[]; reason_code?: string | null; product_id?: number | null; callback_phone?: string | null; callback_name?: string | null }) => Promise<void> | void;
  acwSeconds?: number;
  /** Wrap the form in its own titled Card (default). Set false when the host
   *  already provides a header (e.g. the blocking overlay). */
  chrome?: boolean;
  /**
   * מפתח הטיוטה (ליד / מספר). הבחירות נשמרות מחוץ לרכיב, כך שרינדור מחדש של
   * המסך לא מוחק קוד סיום שכבר נבחר (06/10/2026: "שם סטטוס בסוף שיחה וזה
   * מוריד אותו אחרי 3 שניות").
   */
  draftKey?: string | null;
  /** The other party's number — enables the "סכם עם AI" button, which pulls an
   *  AI summary of the just-ended call's recording. Omit to hide the button. */
  phoneNumber?: string | null;
  /** מוצרי הקמפיין. ריק/חסר ⇒ הבורר לא מוצג כלל. */
  products?: ProductOption[];
}

/**
 * "לחזור למספר אחר" (24/09/2026): הלקוח ביקש שיחזרו לאדם אחר במועד. הנציג
 * רושם מספר, שם ומתי; השרת פותח ליד חדש באותו קמפיין שעולה לראש התור
 * במועדו ומחויג בודד. הקוד קיים כברירת מחדל בכל מרכזייה; אם רשימת
 * הקמפיין החליפה אותו — מוסיפים אותו כאן כדי שהאפשרות לא תיעלם.
 */
export const CALLBACK_OTHER = 'callback_other';
const CALLBACK_OTHER_OPTION: DispositionOption = { code: CALLBACK_OTHER, label: 'לחזור למספר אחר', requires_followup: true, color: '#0891b2' };

/**
 * קודים שלא מוצגים בסיכום שיחה (06/10/2026, דורון): "לא ענה" אינו תוצאה של
 * שיחה שנענתה — המערכת רושמת אותו לבד; ו"דלג" אינו תיעוד בכלל.
 */
const HIDDEN_CODES = new Set(['no_answer', 'skip', 'skipped']);

interface WrapupDraft {
  disposition: string;
  notes: string;
  callbackAt: string;
  reasonCode: string;
  productId: string;
  selectedTags: string[];
  freeTags: string;
  callbackPhone: string;
  callbackName: string;
}

/** טיוטות סיכום לפי ליד — שורדות unmount/remount של הטופס. */
const drafts = new Map<string, WrapupDraft>();
const MAX_DRAFTS = 30;

export function clearWrapupDrafts(): void {
  drafts.clear();
}

/** "YYYY-MM-DDTHH:mm" בשעון המקומי — הפורמט של datetime-local. */
export function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** קיצורי מועד חזרה — לחיצה אחת במקום בורר תאריך. */
function quickCallbackTimes(now = new Date()): Array<{ label: string; value: string }> {
  const inHours = (h: number) => { const d = new Date(now.getTime() + h * 3600_000); d.setSeconds(0, 0); return d; };
  const tomorrowAt = (h: number) => { const d = new Date(now); d.setDate(d.getDate() + 1); d.setHours(h, 0, 0, 0); return d; };
  return [
    { label: 'בעוד שעה', value: toLocalInput(inHours(1)) },
    { label: 'בעוד 3 שעות', value: toLocalInput(inHours(3)) },
    { label: 'מחר 10:00', value: toLocalInput(tomorrowAt(10)) },
    { label: 'מחר 16:00', value: toLocalInput(tomorrowAt(16)) },
  ];
}

/** קצב ההמתנה לתמלול — מיוצא כדי שבדיקות יקצרו אותו. */
export const AI_WAIT = { pollMs: 5000, maxMs: 5 * 60 * 1000 };

export function WrapupInline({ dispositions: dispositionsProp, tagOptions = [], onSave, acwSeconds = 30, chrome = true, phoneNumber, products = [], draftKey }: WrapupInlineProps) {
  const { t } = useTranslation();
  const visible = dispositionsProp.filter((d) => !HIDDEN_CODES.has(d.code));
  const dispositions = visible.some((d) => d.code === CALLBACK_OTHER)
    ? visible
    : [...visible, CALLBACK_OTHER_OPTION];
  const key = draftKey ?? phoneNumber ?? null;
  const [draft] = useState(() => (key ? drafts.get(key) : undefined));
  const [callbackPhone, setCallbackPhone] = useState(draft?.callbackPhone ?? '');
  const [callbackName, setCallbackName] = useState(draft?.callbackName ?? '');
  const [disposition, setDisposition] = useState(draft?.disposition ?? '');
  const [productId, setProductId] = useState(draft?.productId ?? '');
  const [notes, setNotes] = useState(draft?.notes ?? '');
  const [callbackAt, setCallbackAt] = useState(draft?.callbackAt ?? '');
  const [reasonCode, setReasonCode] = useState<string>(draft?.reasonCode ?? '');
  const [selectedTags, setSelectedTags] = useState<string[]>(draft?.selectedTags ?? []);
  const [freeTags, setFreeTags] = useState(draft?.freeTags ?? '');
  const savedRef = useRef(false);
  useEffect(() => {
    if (!key || savedRef.current) return;
    drafts.delete(key);
    drafts.set(key, { disposition, notes, callbackAt, reasonCode, productId, selectedTags, freeTags, callbackPhone, callbackName });
    while (drafts.size > MAX_DRAFTS) drafts.delete(drafts.keys().next().value as string);
  }, [key, disposition, notes, callbackAt, reasonCode, productId, selectedTags, freeTags, callbackPhone, callbackName]);
  const [saving, setSaving] = useState(false);
  /** כשלונות שמירה רצופים — חושפים מוצא חירום כדי לא לכלוא את הסופטפון. */
  const [failures, setFailures] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDone, setAiDone] = useState(false);
  /** ממתינים לתמלול: הכפתור נלחץ לפני שהתמלול הגיע, והסיכום יופק כשיגיע. */
  const [aiWaiting, setAiWaiting] = useState(false);
  const waitSeqRef = useRef(0);
  // יציאה מהטופס מבטלת המתנה — אין למי להגיש את הסיכום.
  useEffect(() => () => { waitSeqRef.current += 1; }, []);
  const [remaining] = useState(acwSeconds);
  const finishWrapup = useSipStore((s) => s.finishWrapup);

  // The chosen code may require a note before it can be saved.
  const chosen = dispositions.find((d) => d.code === disposition);
  const noteRequired = !!chosen?.requires_note;
  const reasonOptions = chosen?.reasons ?? [];
  // סטטוס עם סיבות מוגדרות דורש בחירה — השרת מחזיר 422 בלעדיה.
  const reasonMissing = reasonOptions.length > 0 && !reasonCode;
  const noteMissing = noteRequired && !notes.trim();
  // קוד סיום מסוג פולו-אפ חייב מועד חזרה. בלעדיו הליד נסגר בשקט ואובד —
  // אותה חסימה שקיימת ב-Dialer, כאן כדי שההודעה תגיע מהלקוח ולא כ-422.
  const followupRequired = !!chosen?.requires_followup;
  const followupMissing = followupRequired && !callbackAt;
  const isCallbackOther = disposition === CALLBACK_OTHER;
  const callbackPhoneMissing = isCallbackOther && callbackPhone.replace(/\D/g, '').length < 7;
  // אותה הגדרה כמו ProductCatalog::isDealDisposition בשרת.
  const isDeal = chosen?.lead_status === 'deal_closed' || chosen?.code === 'deal';
  const showProducts = isDeal && products.length > 0;

  function toggleTag(slug: string) {
    setSelectedTags((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  }

  function applyAiSummary(r: AiCallSummary) {
    // Compose summary + highlights into the notes field, appending so the
    // agent's own notes aren't clobbered.
    const block = [
      r.summary?.trim(),
      r.highlights.length ? r.highlights.map((h) => `• ${h}`).join('\n') : '',
    ].filter(Boolean).join('\n\n');
    if (block) {
      setNotes((prev) => (prev.trim() ? `${prev.trim()}\n\n${block}` : block));
    }
    // Pre-select the suggested disposition only if the agent hasn't chosen one.
    if (r.suggested_disposition && !disposition && dispositions.some((d) => d.code === r.suggested_disposition)) {
      setDisposition(r.suggested_disposition);
    }
    setAiDone(true);
    toast(block ? 'הסיכום הופק' : 'לא נמצא תוכן לסיכום', block ? 'ok' : 'warn');
  }

  /**
   * התמלול עוד לא הגיע (ה-job רץ ~30-90 שניות אחרי הניתוק). עד כאן הכפתור
   * נכשל עם "אין תמלול זמין" והנציג נשאר בלי סיכום. עכשיו ממתינים: תשאול
   * כל כמה שניות עד שהתמלול מוכן, ואז הסיכום מופק לבד — בלי לחיצה נוספת
   * ובלי לעזוב את הטופס (24/09/2026: "הכפתור יעבוד כשהתמלול הגיע").
   */
  async function waitForTranscriptThenSummarize(number: string) {
    const seq = ++waitSeqRef.current;
    setAiWaiting(true);
    toast('התמלול עדיין לא הגיע — הסיכום יופק אוטומטית כשיגיע', 'warn');
    const deadline = Date.now() + AI_WAIT.maxMs;
    try {
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, AI_WAIT.pollMs));
        if (seq !== waitSeqRef.current) return;
        let status: string | null = null;
        try {
          status = (await agentApi.transcriptLatest(number))?.transcript_status ?? null;
        } catch {
          // תקלת רשת רגעית — ממשיכים להמתין.
        }
        if (seq !== waitSeqRef.current) return;
        if (status === 'skipped') {
          toast('אין הקלטה לשיחה — לא ניתן להפיק סיכום', 'danger');
          return;
        }
        if (status !== 'done') continue;
        try {
          applyAiSummary(await agentApi.aiCallSummary(number));
        } catch {
          if (seq === waitSeqRef.current) toast('שגיאה בהפקת הסיכום', 'danger');
        }
        return;
      }
      if (seq === waitSeqRef.current) toast('התמלול מתעכב — אפשר לנסות שוב בעוד רגע', 'warn');
    } finally {
      if (seq === waitSeqRef.current) setAiWaiting(false);
    }
  }

  async function summarizeWithAi() {
    if (!phoneNumber || aiWaiting) return;
    setAiLoading(true);
    try {
      applyAiSummary(await agentApi.aiCallSummary(phoneNumber));
    } catch (e) {
      const res = (e as { response?: { status?: number; data?: { code?: string; message?: string } } })?.response;
      const code = res?.data?.code;
      if (res?.status === 422 && (code === 'transcript_pending' || code === undefined)) {
        setAiLoading(false);
        void waitForTranscriptThenSummarize(phoneNumber);
        return;
      }
      if (res?.status === 422 && code === 'no_recording') {
        toast('אין הקלטה לשיחה — לא ניתן להפיק סיכום', 'danger');
      } else {
        toast(res?.status === 422 ? 'אין תמלול זמין לשיחה זו' : 'שגיאה בהפקת הסיכום', 'danger');
      }
    } finally {
      setAiLoading(false);
    }
  }

  async function save() {
    if (!disposition) {
      toast('יש לבחור קוד סיום', 'warn');
      return;
    }
    if (noteMissing) {
      toast('סטטוס זה מחייב הערה', 'warn');
      return;
    }
    if (followupMissing) {
      toast('קוד זה דורש מועד חזרה — בחר תאריך ושעה', 'warn');
      return;
    }
    if (reasonMissing) {
      toast('קוד זה דורש בחירת סיבה', 'warn');
      return;
    }
    if (callbackPhoneMissing) {
      toast('יש להזין את המספר שאליו חוזרים', 'warn');
      return;
    }
    setSaving(true);
    try {
      const tags = tagOptions.length > 0
        ? selectedTags
        : freeTags.split(',').map((s) => s.trim()).filter(Boolean);
      await onSave({
        disposition,
        notes,
        // מועד נשלח רק לקוד שמחייב חזרה — אין יותר "תזכורת" כללית לכל קוד.
        callback_at: followupRequired ? (callbackAt || null) : null,
        tags,
        reason_code: reasonCode || null,
        // נשלח רק בעסקה: השרת דוחה מוצר שאינו בקמפיין, וסטטוס שאינו
        // עסקה מתעלם ממנו ממילא.
        product_id: showProducts && productId ? Number(productId) : null,
        callback_phone: isCallbackOther ? callbackPhone.trim() : null,
        callback_name: isCallbackOther ? (callbackName.trim() || null) : null,
      });
      savedRef.current = true;
      if (key) drafts.delete(key);
      toast('נשמר', 'ok');
      finishWrapup();
    } catch (e) {
      // Surface the server's validation message (e.g. requires_note 422).
      const err = e as { response?: { data?: { message?: string }; status?: number } };
      const msg = err?.response?.data?.message;
      toast(msg ?? t('common.error'), 'danger');
      // מוצא חירום: במצב modal אין כפתור "דלג", ולכן שמירה שנכשלת השאירה
      // את הסופטפון כלוא ב-wrap-up בלי דרך לחייג שוב (רק רענון דף עזר).
      // אחרי שני כשלונות חושפים יציאה — התיעוד חשוב, אבל לא במחיר קו מת.
      setFailures((n) => n + 1);
    } finally {
      setSaving(false);
    }
  }

  const body = (
      <div className="stack">
        <FormField label="קוד סיום" required>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {dispositions.map((d) => (
              <button
                key={d.code}
                type="button"
                className={`btn ${disposition === d.code ? 'primary' : ''}`}
                onClick={() => setDisposition(d.code)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </FormField>

        {isCallbackOther && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <FormField label="למי לחזור — מספר" required error={callbackPhoneMissing && callbackPhone ? 'מספר לא תקין' : undefined}>
              <TextInput
                type="tel"
                dir="ltr"
                value={callbackPhone}
                onChange={(e) => setCallbackPhone(e.target.value)}
                placeholder="05x-xxx-xxxx"
                aria-label="מספר לחזרה"
              />
            </FormField>
            <FormField label="שם">
              <TextInput
                value={callbackName}
                onChange={(e) => setCallbackName(e.target.value)}
                placeholder="שם איש הקשר"
                aria-label="שם לחזרה"
              />
            </FormField>
          </div>
        )}

        {/* מועד חזרה — רק לקוד שמחייב חזרה, ומיד מתחת לבחירה. עד 06/10/2026
            הבורר ישב בתחתית הטופס תחת "חזרה לתזכורת" ולכל קוד, ו"חזרה בהמשך"
            נראה כאילו אין לו תאריך ושעה בכלל. */}
        {followupRequired && (
          <div data-testid="callback-picker" style={{
            border: '1px solid var(--accent)', borderRadius: 'var(--r-3)', padding: 10,
            background: 'var(--accent-soft)',
          }}>
            <FormField
              label="מתי לחזור — תאריך ושעה"
              required
              error={followupMissing ? 'קוד זה דורש מועד חזרה' : undefined}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {quickCallbackTimes().map((q) => (
                  <button
                    key={q.label}
                    type="button"
                    className={`btn btn-sm ${callbackAt === q.value ? 'primary' : ''}`}
                    onClick={() => setCallbackAt(q.value)}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
              <TextInput
                type="datetime-local"
                aria-label="מועד חזרה"
                min={toLocalInput(new Date())}
                value={callbackAt}
                onChange={(e) => setCallbackAt(e.target.value)}
              />
            </FormField>
          </div>
        )}

        <FormField label="הערות" required={noteRequired} error={noteMissing ? 'סטטוס זה מחייב הערה' : undefined}>
          {phoneNumber && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
              <button
                type="button"
                className="btn ghost"
                onClick={summarizeWithAi}
                disabled={aiLoading || aiWaiting}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)' }}
                title="הפק סיכום אוטומטי מהקלטת השיחה"
              >
                <Icon name="ai" size={14} color="var(--accent)" />
                {aiLoading ? 'מסכם…' : aiWaiting ? 'ממתין לתמלול…' : aiDone ? 'סכם שוב עם AI' : 'סכם עם AI'}
              </button>
            </div>
          )}
          <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="סיכום השיחה…" />
        </FormField>

        {phoneNumber && (
          <FormField label="הערות מהירות" hint="כל שורה נשמרת מיד — כמו צ'אט">
            <NotesChat phone={phoneNumber} maxHeight={180} />
          </FormField>
        )}

        <FormField label="תיוגים">
          {tagOptions.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {tagOptions.map((tag) => {
                const on = selectedTags.includes(tag.slug);
                return (
                  <button
                    key={tag.slug}
                    type="button"
                    onClick={() => toggleTag(tag.slug)}
                    style={{
                      padding: '5px 12px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                      fontSize: 'var(--t-sm)', fontWeight: on ? 600 : 500,
                      border: `1px solid ${on ? 'transparent' : 'var(--line)'}`,
                      background: on ? (tag.color || 'var(--accent-soft)') : 'var(--surface)',
                      color: on ? (tag.color ? 'var(--on-accent)' : 'var(--accent-ink)') : 'var(--text-2)',
                    }}
                  >
                    {tag.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <TextInput value={freeTags} onChange={(e) => setFreeTags(e.target.value)} placeholder="תיוגים מופרדים בפסיק" />
          )}
        </FormField>

        {reasonOptions.length > 0 && (
          <FormField
            label="סיבה"
            required
            error={reasonMissing ? 'קוד זה דורש בחירת סיבה' : undefined}
          >
            <select
              className="input"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              aria-label="סיבה"
            >
              <option value="">בחר סיבה…</option>
              {reasonOptions.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </FormField>
        )}

        {showProducts && (
          <FormField label="מוצר" hint="מה נמכר בעסקה. ניתן להשאיר ריק.">
            <select
              className="input"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              aria-label="מוצר"
            >
              <option value="">ללא מוצר</option>
              {products.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>
          </FormField>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {/* אין "דלג" (06/10/2026): כל שיחה מתועדת. מוצא חירום רק כשהשמירה
              עצמה נכשלת פעמיים — אחרת הקו נשאר כלוא ב-wrap-up. */}
          {failures >= 2 && (
            <button
              type="button"
              className="btn"
              onClick={() => finishWrapup()}
              title="השמירה נכשלת — סגירה בלי תיעוד כדי לשחרר את הקו"
            >
              סגור בלי לשמור
            </button>
          )}
          <button type="button" className="btn primary" onClick={save} disabled={saving || !disposition || noteMissing || followupMissing || reasonMissing || callbackPhoneMissing}
            title={!disposition ? 'בחר קוד סיום' : noteMissing ? 'סטטוס זה מחייב הערה' : followupMissing ? 'קוד זה דורש מועד חזרה' : undefined}
            style={{ minWidth: 160, height: 44 }}>
            {saving ? t('common.loading') : 'שמור וסיים'}
          </button>
        </div>
      </div>
  );

  if (!chrome) return body;
  return (
    <Card title="סיכום שיחה" action={<StatusBadge tone="info">ACW: {remaining}s</StatusBadge>}>
      {body}
    </Card>
  );
}
